import { useLayoutEffect } from 'react'
import { Maximize2 } from 'lucide-react'
import { Modal } from '@/components/ui'
import { cn } from '@/lib/cn'

const INTERACTIVE =
  'a,button,input,select,textarea,label,[role="button"],[role="radio"],[data-no-expand]'

export interface DashboardTileProps {
  title: string
  subtitle?: React.ReactNode
  /** Leading glyph beside the title. */
  icon?: React.ComponentType<{ className?: string }>
  /** Trailing header controls (legend, count, link). Never triggers expand. */
  actions?: React.ReactNode
  /** Opens the card's detail view. Omit for a card with nothing more to show. */
  onExpand?: () => void
  footer?: React.ReactNode
  className?: string
  bodyClassName?: string
  children: React.ReactNode
}

/**
 * A dashboard section that grows into a detail modal. The expand button is
 * the keyboard path; a click anywhere on the body that isn't itself a control
 * (or the end of a text selection) is the pointer shortcut.
 */
export function DashboardTile({
  title,
  subtitle,
  icon: Icon,
  actions,
  onExpand,
  footer,
  className,
  bodyClassName,
  children,
}: DashboardTileProps): React.ReactElement {
  function handleBodyClick(event: React.MouseEvent<HTMLDivElement>): void {
    if (!onExpand) return
    if ((event.target as HTMLElement).closest(INTERACTIVE)) return
    if (window.getSelection()?.toString()) return
    onExpand()
  }

  return (
    <section
      className={cn(
        'flex min-w-0 flex-col rounded-[var(--radius-panel)] border border-gray-200 bg-white shadow-[var(--shadow-panel)]',
        'dark:border-gray-700/80 dark:bg-gray-800 dark:shadow-none',
        onExpand &&
          'transition-[box-shadow,border-color] duration-150 ease-out hover:border-gray-300 hover:shadow-[var(--shadow-panel-raised)] dark:hover:border-gray-600',
        className
      )}
    >
      <header className="flex items-start justify-between gap-3 px-5 pb-3 pt-4">
        <div className="flex min-w-0 items-start gap-2">
          {Icon ? (
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary dark:text-sky-300" aria-hidden />
          ) : null}
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold tracking-[-0.006em] text-gray-900 dark:text-gray-100">
              {title}
            </h3>
            {subtitle ? (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {onExpand ? (
            <button
              type="button"
              onClick={onExpand}
              aria-label={`Expand ${title}`}
              title="Expand"
              className="-mr-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
      </header>
      {/* Pointer shortcut only; the expand button above is the accessible path. */}
      <div
        onClick={handleBodyClick}
        className={cn('min-h-0 flex-1', onExpand && 'cursor-pointer', bodyClassName)}
      >
        {children}
      </div>
      {footer ? (
        <div className="border-t border-gray-100 px-5 py-3 dark:border-gray-700/70">{footer}</div>
      ) : null}
    </section>
  )
}

/** The large detail view every dashboard card expands into. */
export function DashboardDetailModal({
  open,
  onClose,
  title,
  subtitle,
  toolbar,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: React.ReactNode
  /** Filters and actions pinned above the content. */
  toolbar?: React.ReactNode
  children: React.ReactNode
}): React.ReactElement | null {
  // Locking scroll removes the page scrollbar; pad the body by its width so
  // the dashboard behind the backdrop doesn't jump sideways. Runs as a layout
  // effect (before Modal's own lock) so the scrollbar is still there to measure,
  // and unlocks in the same frame it unpads on close.
  useLayoutEffect(() => {
    if (!open) return undefined
    const { body, documentElement } = document
    const gap = window.innerWidth - documentElement.clientWidth
    const previous = { overflow: body.style.overflow, paddingRight: body.style.paddingRight }
    body.style.overflow = 'hidden'
    if (gap > 0) body.style.paddingRight = `${gap}px`
    return () => {
      body.style.overflow = previous.overflow
      body.style.paddingRight = previous.paddingRight
    }
  }, [open])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={null}
      className="dashboard-modal-in max-w-6xl sm:min-h-[min(70dvh,40rem)]"
    >
      {subtitle || toolbar ? (
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          {subtitle ? (
            <p className="max-w-prose text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
          ) : (
            <span />
          )}
          {toolbar ? <div className="flex flex-wrap items-center gap-2">{toolbar}</div> : null}
        </div>
      ) : null}
      {children}
    </Modal>
  )
}
